import type { FormEvent } from 'react';
import type { FilterValue } from '../../components/GridFiltersManager/filterDefinitions';

import { useStore } from '@tanstack/react-form';
import { use } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';

import { getTestProps } from '../../lib/helpers';

import CancelButton from '../../components/Button/predefined/CancelButton/CancelButton';
import SaveButton from '../../components/Button/predefined/SaveButton/SaveButton';

import { ModalInstanceContext } from '../../contexts/ModalContext';

import { useAppForm } from '../../form-hooks/form';

const SPACE_PLAN_FILTER = ['all', 'minOneFree', 'minOnePaid'];

const INITIAL_VALUES = {
  includeTableFilters: true,
  excludeAppSumo: true,
  spacePlan: 'minOnePaid',
  excludeIds: true,
  idsToExclude: '',
  excludeInternal: true,
};

interface UsersCustomExportModalFormProps {
  /**
   * On submit handler
   */
  onSubmit: (
    values: Record<string, string | string[] | boolean>,
  ) => Promise<boolean>;
  /**
   * Current table filters to include in export
   */
  tableFilters: Record<string, FilterValue | undefined>;
  /**
   * Test id for testing purposes
   */
  testId?: string;
}

const UsersCustomExportModalForm = ({
  onSubmit,
  tableFilters,
  testId,
}: UsersCustomExportModalFormProps) => {
  const { t } = useTranslation();
  const modalInstace = use(ModalInstanceContext);

  const validationSchema = z.object({
    includeTableFilters: z.boolean(),
    excludeAppSumo: z.boolean(),
    excludeInternal: z.boolean(),
    spacePlan: z.enum(SPACE_PLAN_FILTER),
    excludeIds: z.boolean(),
    idsToExclude: z.string(),
  });

  const spaceFilterOptions = SPACE_PLAN_FILTER.map((option) => ({
    label: t(`UsersCustomExportModal.SpacePlanFilterOptions.${option}`),
    value: option,
  }));

  const closeModal = () => {
    modalInstace.resolve?.();
  };

  const form = useAppForm({
    defaultValues: INITIAL_VALUES,
    onSubmit: async ({ value }) => {
      const idsToExclude = value.excludeIds
        ? value.idsToExclude
            .split(/[,;\n]/)
            .map((id) => id.trim())
            .filter((id) => id !== '')
            .join(',')
        : '';

      const finalFilters = {
        ...(value.includeTableFilters ? tableFilters : {}),
        filterBySpacesPlan: value.spacePlan,
        excludeAppSumo: value.excludeAppSumo,
        ...(idsToExclude ? { excludedIds: idsToExclude } : {}),
        excludeInternal: value.excludeInternal,
      };

      const ok = await onSubmit(finalFilters);
      if (ok) {
        closeModal();
      }
    },
    validators: {
      onChange: validationSchema,
    },
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
  };

  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);

  return (
    <form.AppForm>
      <form
        className="space-y-3"
        id="export-users-form"
        onSubmit={handleSubmit}
        noValidate
        {...getTestProps(testId, 'form')}
      >
        <form.AppField name="includeTableFilters">
          {(field) => (
            <field.Switch
              label={t('UsersCustomExportModal.IncludeTableFilters')}
              disabled={isSubmitting}
            />
          )}
        </form.AppField>
        <form.AppField name="excludeAppSumo">
          {(field) => (
            <field.Switch
              label={t('UsersCustomExportModal.ExcludeAppSumo')}
              disabled={isSubmitting}
            />
          )}
        </form.AppField>
        <form.AppField name="excludeInternal">
          {(field) => (
            <field.Switch
              label={t('UsersCustomExportModal.ExcludeInternal')}
              disabled={isSubmitting}
            />
          )}
        </form.AppField>

        <form.AppField name="excludeIds">
          {(field) => (
            <field.Switch
              label={t('UsersCustomExportModal.ExcludeIds')}
              disabled={isSubmitting}
            />
          )}
        </form.AppField>
        <form.Subscribe selector={({ values }) => values.excludeIds}>
          {(include) =>
            include ? (
              <form.AppField name="idsToExclude">
                {(field) => (
                  <field.Textarea
                    label={t('UsersCustomExportModal.IdsToExclude')}
                    disabled={isSubmitting}
                  />
                )}
              </form.AppField>
            ) : null
          }
        </form.Subscribe>

        <form.AppField name="spacePlan">
          {(field) => (
            <field.Dropdown
              options={spaceFilterOptions}
              label={t('UsersCustomExportModal.SpacePlanFilter')}
              disabled={isSubmitting}
            />
          )}
        </form.AppField>

        <div className="absolute left-0 bottom-0 flex flex-row w-full px-10 py-5 border-t">
          <CancelButton onClick={closeModal} />
          <SaveButton
            type="submit"
            additionalClasses={'ml-auto'}
            label={t('Users.Export')}
            {...getTestProps(testId, 'submit', 'testId')}
          />
        </div>
      </form>
    </form.AppForm>
  );
};

export default UsersCustomExportModalForm;
